/**
 * Web i18n entry point.
 *
 * Call sites consume the active catalog directly via the re-exported
 * `messages` object — `messages.header.statusConnected`,
 * `messages.list.noMatchesDesc(query)`, etc.
 *
 * Why direct object access (and no `t("path.string")` helper):
 *   - The catalog already gives TypeScript-narrow access — typos
 *     (`messages.headre.banner`) are a compile error today, no
 *     `Path<Messages>` typing plumbing required.
 *   - Functions for plurals / interpolation read the same as a `t()`
 *     wrapper (`messages.list.clearMatches(7)`) without a layer of
 *     indirection.
 *
 * Runtime locale picker: `messages` is resolved ONCE at module load
 * from a registry keyed by the browser locale (`detectLocale()`), with
 * English as the fallback for unregistered locales. Because it's picked
 * once and re-exported behind the same object surface, call sites never
 * change when a locale is added — only the registry below grows. The
 * settings-page language picker feeds `detectLocale()` via the
 * `LANGUAGE_OVERRIDE_KEY` localStorage value and reloads the page —
 * consumers never change.
 *
 * Adding a locale: drop `messages.<lang>.ts` (mirroring the catalog
 * shape via `satisfies MessagesShape`), then add one entry to the
 * `catalogs` registry below. The `Messages` / `MessagesShape` types are
 * derived from the EN catalog, so a missing or renamed key in a locale
 * file is a TypeScript error across every locale at once.
 *
 * Out of scope for this catalog: log scope strings, structured
 * event names, JSDoc, server-emitted error messages — see
 * `messages.en.ts` header for the full exclusion list.
 */

import { messages as messagesCs } from "./messages.cs";
import { messages as messagesDa } from "./messages.da";
import { messages as messagesDe } from "./messages.de";
import { messages as messagesEl } from "./messages.el";
import { messages as messagesEn } from "./messages.en";
import { messages as messagesEs } from "./messages.es";
import { messages as messagesFi } from "./messages.fi";
import { messages as messagesFr } from "./messages.fr";
import { messages as messagesHi } from "./messages.hi";
import { messages as messagesHu } from "./messages.hu";
import { messages as messagesId } from "./messages.id";
import { messages as messagesIt } from "./messages.it";
import { messages as messagesJa } from "./messages.ja";
import { messages as messagesKo } from "./messages.ko";
import { messages as messagesMs } from "./messages.ms";
import { messages as messagesNb } from "./messages.nb";
import { messages as messagesNl } from "./messages.nl";
import { messages as messagesPl } from "./messages.pl";
import { messages as messagesPtBr } from "./messages.pt-BR";
import { messages as messagesRo } from "./messages.ro";
import { messages as messagesRu } from "./messages.ru";
import { messages as messagesSv } from "./messages.sv";
import { messages as messagesTh } from "./messages.th";
import { messages as messagesTr } from "./messages.tr";
import { messages as messagesUk } from "./messages.uk";
import { messages as messagesVi } from "./messages.vi";
import { messages as messagesZhCn } from "./messages.zh-CN";
import { messages as messagesZhTw } from "./messages.zh-TW";

/**
 * Registry of every wired locale catalog. Keys are the locale tags the
 * picker matches `navigator.language` against; `en` is the fallback.
 * Each catalog conforms to `MessagesShape` (enforced structurally by
 * each locale file's own `satisfies MessagesShape`).
 *
 * Exported so the i18n test suite can walk every registered locale's
 * function leaves (the function-arg lint that asserts a translation never
 * silently ignores a parameter like `count`).
 */
export const catalogs = {
  cs: messagesCs,
  da: messagesDa,
  de: messagesDe,
  el: messagesEl,
  en: messagesEn,
  es: messagesEs,
  fi: messagesFi,
  fr: messagesFr,
  hi: messagesHi,
  hu: messagesHu,
  id: messagesId,
  it: messagesIt,
  ja: messagesJa,
  ko: messagesKo,
  ms: messagesMs,
  nb: messagesNb,
  nl: messagesNl,
  pl: messagesPl,
  "pt-BR": messagesPtBr,
  ro: messagesRo,
  ru: messagesRu,
  sv: messagesSv,
  th: messagesTh,
  tr: messagesTr,
  uk: messagesUk,
  vi: messagesVi,
  "zh-CN": messagesZhCn,
  "zh-TW": messagesZhTw,
};

/** A locale tag registered in `catalogs`. */
export type Locale = keyof typeof catalogs;

const DEFAULT_LOCALE: Locale = "en";

/**
 * localStorage key holding the user's language override (a `catalogs` tag).
 * localStorage — not the StorageAdapter — because this module resolves the
 * catalog synchronously at import time, before IndexedDB can answer (the
 * INSTALL_DISMISSED_KEY exception class). The settings page is the only
 * writer and mirrors the value to `ReflectoStorage.languageOverride` so the
 * service worker (which has no localStorage) can localize its toasts. See
 * docs/superpowers/specs/2026-07-13-language-picker-design.md.
 */
export const LANGUAGE_OVERRIDE_KEY = "reflecto-language";

/** Read the override; null when unset, unreadable, or storage is blocked. */
export function readLanguageOverride(): string | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(LANGUAGE_OVERRIDE_KEY) : null;
  } catch {
    return null;
  }
}

/**
 * Pick the active locale from the browser environment.
 *
 * Exact tag match first (`"es"` → `es`), then a base-language match
 * (`"es-MX"` → `es`), else the `en` fallback. Guards
 * `typeof navigator === "undefined"` so the module is import-safe in
 * non-DOM contexts (SSR, tests, workers). Exported for tests.
 */
export function detectLocale(): Locale {
  // User override wins over the browser language; an unregistered stored
  // tag (e.g. a locale later removed) falls through to browser detection.
  const override = readLanguageOverride();
  if (override && override in catalogs) return override as Locale;
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  const raw = navigator.language;
  if (!raw) return DEFAULT_LOCALE;
  if (raw in catalogs) return raw as Locale;
  const base = raw.split("-")[0];
  if (base && base in catalogs) return base as Locale;
  return DEFAULT_LOCALE;
}

/**
 * The active catalog, resolved once at module load. Typed as the widened
 * `MessagesShape` (not `typeof messagesEn`) because the value is picked
 * at runtime and may be any registered locale — consumers only read
 * keys, so the narrowed literal strings aren't needed.
 */
export const messages: MessagesShape = catalogs[detectLocale()];

export type Messages = typeof messages;

/**
 * Structural shape of the catalog — every string-literal in the EN
 * catalog widened to `string`, every function preserved as-is. Sibling
 * locale files (`messages.es.ts`, future `messages.fr.ts`, …)
 * `satisfies` this type so missing or renamed keys in EN are a
 * TypeScript error across every locale at once. `satisfies` (not
 * `: MessagesShape =`) preserves the literal translated strings on each
 * exported object so tooling that reads the file at compile time still
 * gets the exact source text rather than `string`.
 *
 * Derived from the EN catalog (`messages.en.ts`) — the source of truth —
 * rather than the active `messages` value, so adding a top-level group
 * in EN propagates automatically and the type never depends on which
 * locale happens to be active at runtime.
 */
type Widen<T> = T extends string
  ? string
  : T extends (...args: infer A) => infer R
    ? (...args: A) => R
    : T extends object
      ? { [K in keyof T]: Widen<T[K]> }
      : T;

export type MessagesShape = Widen<typeof messagesEn>;
